import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useMemo, useState } from 'react';

import { coachDisplayName } from '@/components/CoachSummary';
import { trackEvent, useFeatureFlagVariant } from '@/modules/analytics';
import type { MatchedCoach } from '@/modules/api';
import { FEATURE_FLAGS, useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, Screen, ScreenHeading } from '@/modules/ui';

import { CoachBioSheet } from './components/CoachBioSheet';
import { CoachListSkeleton } from './components/CoachListSkeleton';
import { CompactCoachCard } from './components/CompactCoachCard';
import { RecommendedCoachCard } from './components/RecommendedCoachCard';

/**
 * SELECT COACH — the matched-coach list (Sparta's ProvidersListContainer /
 * vida-design-system CoachMatching demo). Coaches are auto-matched server-side
 * (no preference inputs); the highest match is surfaced as the "Best fit"
 * recommended card, the rest under "More health experts". The catalog loads
 * non-blocking so the screen shows a skeleton; selecting + Continue records the
 * pick on the rules engine and advances to coach availability.
 */
export function CoachMatchingScreen() {
  const t = useT();
  const {
    matchedCoaches,
    isLoadingCoaches,
    selectedCoachUuid,
    setSelectedCoach,
    setScheduledConsult,
    assignPrimaryCoach,
    setCurrentRoute,
    coachAvailability,
  } = useRulesEngine();

  // Render-time flag, evaluated on mount (against the identified user):
  // `recommendTopPick` surfaces the highlighted "Best fit" card vs a flat list.
  // Hold the list skeleton until it resolves (see `flagsLoading`) rather than
  // assuming "off" mid-read, which would flash a flat list. Initial-consult
  // scheduling + quick-intro are Split-gated in Sparta; onboarding has no Split,
  // so they're enabled by default here (no flag read) — see featureFlags note.
  const { value: topPickVariant, isLoading: flagsLoading } = useFeatureFlagVariant<
    'control' | 'test'
  >(FEATURE_FLAGS.COACH_HARD_RECOMMEND_TOP_PICK);
  // 'test' is the enabled cohort of this PostHog multivariate flag (vs 'control').
  const recommendTopPick = topPickVariant === 'test';

  const [bioCoach, setBioCoach] = useState<MatchedCoach | null>(null);
  const [isPairing, setIsPairing] = useState(false);
  const [pairError, setPairError] = useState(false);

  const { recommended, others, allCoaches, selected } = useMemo(() => {
    const coaches = matchedCoaches?.coaches ?? [];
    // The first matched coach is the best fit (the list comes back ranked).
    const rec = coaches[0];
    return {
      recommended: rec,
      others: coaches.slice(1),
      allCoaches: coaches,
      // Pre-select the top pick only when it's hard-recommended; otherwise the
      // user picks from a flat list with no default (Sparta's top-pick flag off).
      selected: selectedCoachUuid ?? (recommendTopPick ? rec?.uuid : undefined),
    };
  }, [matchedCoaches, selectedCoachUuid, recommendTopPick]);

  const advance = async (coachUuid: string) => {
    setSelectedCoach(coachUuid);
    // Mirror Sparta's selectCoachEpic: show the scheduling screen unless the
    // coach has a *known* lack of availability, in which case pair directly and
    // skip to quick-intro. While availability is still unknown (not yet loaded),
    // default to the scheduling screen — it handles its own loading / empty /
    // skip states. Availability comes from the shared rules-engine cache the
    // cards populated. (Consult scheduling + quick-intro are Split-gated in
    // Sparta; enabled by default here — see featureFlags note.)
    const coach = matchedCoaches?.coaches.find((c) => c.uuid === coachUuid);
    const availabilityState = coach ? coachAvailability[coach.userUuid] : undefined;
    const knownNoSlots =
      availabilityState?.status === 'ready' && availabilityState.value.timeslots.length === 0;
    if (knownNoSlots) {
      setPairError(false);
      try {
        setIsPairing(true);
        setScheduledConsult(undefined);
        await assignPrimaryCoach(coachUuid);
        // No consult booked → on to the quick-intro step.
        setCurrentRoute('/step/quick-intro-messages');
      } catch {
        setPairError(true);
        setIsPairing(false);
      }
      return;
    }
    setCurrentRoute('/step/coach-availability');
  };

  // Selection is keyed by the coach-profile `uuid` (card identity), but the
  // analytics `coachUuid` is the coach's USER uuid (Sparta sends user.uuid for
  // every coach event), so resolve it from the picked profile.
  const onSelectCoach = (coachUuid: string) => {
    setSelectedCoach(coachUuid);
    const coach = matchedCoaches?.coaches.find((c) => c.uuid === coachUuid);
    trackEvent('coach-matching-selected-coach', {
      coachUuid: coach?.userUuid,
      batchUuid: matchedCoaches?.batchUuid,
    });
  };

  const onReadBio = (coach: MatchedCoach) => {
    setBioCoach(coach);
    trackEvent('coach-matching-open-full-bio-drawer', { coachUuid: coach.userUuid });
  };

  const selectedCoach = matchedCoaches?.coaches.find((c) => c.uuid === selected);
  const selectedName = selectedCoach ? coachDisplayName(selectedCoach) : undefined;

  return (
    <Screen
      busy={isPairing}
      footer={
        <CtaButton
          isBusy={isPairing}
          isDisabled={isLoadingCoaches || flagsLoading || isPairing || !selected}
          onPress={() => {
            if (!selected) return;
            trackEvent('coach-matching-click-continue', { coachUuid: selectedCoach?.userUuid });
            void advance(selected);
          }}
          testID='coach-matching-continue'
        >
          {selectedName ? (
            <T _str='Continue with {name}' name={selectedName} />
          ) : (
            <T _str='Continue' />
          )}
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('Find your best match')}
          subtitle={t(
            'Every Vida expert is a certified professional. You can change your selection at any time.'
          )}
        />

        {pairError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong. Please try again.' />
          </Text>
        ) : null}

        {isLoadingCoaches || flagsLoading ? (
          // Hold the skeleton until the coaches AND the layout flags resolve, so
          // we never flash a flat list / hidden appointment line by assuming off.
          <CoachListSkeleton />
        ) : allCoaches.length === 0 ? (
          <Text size='md' className='text-typography-700'>
            <T _str='We couldn’t load coaches right now. Please refresh and try again.' />
          </Text>
        ) : recommendTopPick && recommended ? (
          // Hard-recommend on: the top match as the highlighted "Best fit" card,
          // the rest under "More health experts".
          <VStack space='lg'>
            <RecommendedCoachCard
              coach={recommended}
              selected={selected === recommended.uuid}
              onPress={() => onSelectCoach(recommended.uuid)}
              onReadBio={() => onReadBio(recommended)}
            />

            {others.length > 0 ? (
              <Text size='md' weight='semibold' className='mt-6 text-typography-900'>
                <T _str='More health experts' />
              </Text>
            ) : null}

            {others.map((coach) => (
              <CompactCoachCard
                key={coach.uuid}
                coach={coach}
                selected={selected === coach.uuid}
                onPress={() => onSelectCoach(coach.uuid)}
                onReadBio={() => onReadBio(coach)}
              />
            ))}
          </VStack>
        ) : (
          // Hard-recommend off: a flat list with no highlighted pick.
          <VStack space='lg'>
            {allCoaches.map((coach) => (
              <CompactCoachCard
                key={coach.uuid}
                coach={coach}
                selected={selected === coach.uuid}
                onPress={() => onSelectCoach(coach.uuid)}
                onReadBio={() => onReadBio(coach)}
              />
            ))}
          </VStack>
        )}
      </VStack>

      <CoachBioSheet
        coach={bioCoach}
        isOpen={bioCoach !== null}
        isTopCoach={bioCoach != null && bioCoach.uuid === recommended?.uuid}
        onClose={() => setBioCoach(null)}
        onContinue={(coach) => {
          setBioCoach(null);
          trackEvent('coach-matching-click-continue-from-full-bio-drawer', {
            coachUuid: coach.userUuid,
          });
          advance(coach.uuid);
        }}
      />
    </Screen>
  );
}
